import type Echo from 'laravel-echo'
import type Pusher from 'pusher-js'
import { captureApiInitiator, traceLaravelApi } from '~/utils/apiTrace'

/**
 * Laravel Echo client wired to the Reverb (Pusher-protocol) server.
 *
 * - `/app` admin channels → Sanctum `POST /broadcasting/auth`
 * - Storefront party channels → public store-chat auth that accepts either
 *   Sanctum bearer or `X-Guest-Token` (so guests get realtime too)
 *
 * Call `$ensureEcho()` from chat surfaces. Do NOT start Echo from this plugin
 * for `/store` (or return a Promise): that downloaded pusher-js before first
 * paint and blocked Nuxt hydration — ~1s+ of TBT on every storefront Lighthouse
 * run.
 */

/** `private-chat.store.{storeId}.party.{chatId}` */
function parseStorePartyChannel(channelName: string): { storeId: string, chatId: string } | null {
  const m = channelName.match(/^private-chat\.store\.(\d+)\.party\.(\d+)$/)
  if (!m) return null
  return { storeId: m[1]!, chatId: m[2]! }
}

/** Cap parallel `/broadcasting/auth` posts so a reconnect cannot 429. */
const AUTH_CONCURRENCY = 2
let authInFlight = 0
const authWaiters: Array<() => void> = []

function acquireAuthSlot(): Promise<void> {
  if (authInFlight < AUTH_CONCURRENCY) {
    authInFlight++
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    authWaiters.push(() => {
      authInFlight++
      resolve()
    })
  })
}

function releaseAuthSlot() {
  authInFlight = Math.max(0, authInFlight - 1)
  const next = authWaiters.shift()
  if (next) next()
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const { loadToken } = useApi()
  const connected = useState('reverb-connected', () => false)
  /** Per-store guest tokens published by `useStoreChat` for Echo auth. */
  const guestTokens = useState<Record<string, string>>('store-chat-guest-tokens', () => ({}))

  let started: Promise<void> | null = null

  function start(): Promise<void> {
    if (started) return started

    started = (async () => {
      const [{ default: EchoClient }, { default: PusherClient }] = await Promise.all([
        import('laravel-echo'),
        import('pusher-js'),
      ])

      ;(window as unknown as { Pusher: typeof Pusher }).Pusher = PusherClient

      const apiBase = String(config.public.apiBase || '').replace(/\/$/, '')
      const sanctumAuthEndpoint = `${apiBase}/broadcasting/auth`
      const scheme = String(config.public.reverbScheme || 'http')
      const port = Number(config.public.reverbPort || 8080)

      const echo = new EchoClient({
        broadcaster: 'reverb',
        key: config.public.reverbKey,
        wsHost: config.public.reverbHost,
        wsPort: port,
        wssPort: port,
        forceTLS: scheme === 'https',
        enabledTransports: ['ws', 'wss'],
        authorizer: (channel: { name: string }) => ({
          authorize: (socketId: string, callback: (error: unknown, data: unknown) => void) => {
            const party = parseStorePartyChannel(channel.name)
            const bearer = loadToken()
            const headers: Record<string, string> = {
              Accept: 'application/json',
            }
            if (bearer) headers.Authorization = `Bearer ${bearer}`

            let endpoint = sanctumAuthEndpoint
            if (party) {
              endpoint = `${apiBase}/public/online-stores/${party.storeId}/store-chat/broadcasting/auth`
              const guest = guestTokens.value[party.storeId]
              if (guest) headers['X-Guest-Token'] = guest
            }

            const initiator = captureApiInitiator() || 'plugins/echo.client.ts'
            void acquireAuthSlot()
              .then(() => $fetch.raw(endpoint, {
                method: 'POST',
                credentials: 'include',
                headers,
                body: { socket_id: socketId, channel_name: channel.name },
              }))
              .then((res) => {
                traceLaravelApi('POST', endpoint, res.status, initiator, res.headers)
                callback(null, res._data)
              })
              .catch((error: { status?: number, statusCode?: number, response?: { status?: number, headers?: unknown } }) => {
                const status = error.response?.status ?? error.status ?? error.statusCode ?? 0
                traceLaravelApi('POST', endpoint, status, initiator, error.response?.headers)
                callback(error, null)
              })
              .finally(releaseAuthSlot)
          },
        }),
      })

      try {
        const connection = echo.connector?.pusher?.connection
        if (connection) {
          const sync = (state?: string) => {
            connected.value = (state ?? connection.state) === 'connected'
          }
          sync()
          connection.bind('state_change', (states: { current: string }) => {
            sync(states.current)
          })
          connection.bind('connected', () => sync('connected'))
          connection.bind('disconnected', () => sync('disconnected'))
          connection.bind('unavailable', () => sync('unavailable'))
          connection.bind('failed', () => sync('failed'))
        }
      }
      catch {
        connected.value = false
      }

      nuxtApp.provide('echo', echo as Echo<'reverb'>)
    })()

    return started
  }

  nuxtApp.provide('ensureEcho', () => start())
})
